/** @jsxImportSource @emotion/react */
import React from 'react'
import { RightOutlined } from '@ant-design/icons'
import {
  MyPlayer,
  MyTeamOfficial,
  UpcomingGame,
} from '@sportninja/common/types/PlayerAttendance'
import {
  cardContainerStyle,
  headerStyle,
  titleStyle,
  viewMoreStyle,
  viewMoreTextStyle,
} from './styles'
import TeamsContainer from './components/TeamsContainer'
import GameInfoComponent from './components/GameInfo'
import TeamSection from './components/TeamSection'
import { trackEvent, getPlatformType } from '../../utils/analytics'

export interface GameAttendanceCardProps {
  isGameTimeToBeDefined: boolean
  gameDateWithoutTime: string | null
  homeTeam: {
    name: string
    logo: string
  }
  awayTeam: {
    name: string
    logo: string
  }
  gameTime: string
  leagueName: string
  arenaName: string
  facilityNumber: string
  onAttendanceSelect: (params: {
    playerId: string
    gameId: string
    rosterId: string
    status: number
  }) => void
  isPlayingOnHomeTeam: boolean
  isPlayingOnAwayTeam: boolean
  playersAttendance: MyPlayer[]
  teamRepsAttendance: MyTeamOfficial[]
  rowItem: UpcomingGame
  isFirstGame: boolean
  onPressManageAttendance: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean
  ) => void
  onPressManageRefreshments: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean
  ) => void
  onPressManageVideographer?: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    teamSide: 'home' | 'visitor'
  ) => void
}

const GameAttendanceCard: React.FC<GameAttendanceCardProps> = ({
  isFirstGame,
  rowItem,
  homeTeam,
  awayTeam,
  gameTime,
  leagueName,
  arenaName,
  facilityNumber,
  onAttendanceSelect,
  isPlayingOnHomeTeam,
  isPlayingOnAwayTeam,
  playersAttendance,
  teamRepsAttendance,
  onPressManageAttendance,
  onPressManageRefreshments,
  onPressManageVideographer,
  isGameTimeToBeDefined = false,
  gameDateWithoutTime = null,
}) => {
  const homePlayers =
    playersAttendance?.filter((p) => p.team_id === rowItem?.home_team?.id) ?? []
  const awayPlayers =
    playersAttendance?.filter(
      (p) => p.team_id === rowItem?.visiting_team?.id
    ) ?? []

  const homeTeamRep =
    teamRepsAttendance?.find((t) => t.team_id === rowItem?.home_team?.id) ??
    null
  const awayTeamRep =
    teamRepsAttendance?.find((t) => t.team_id === rowItem?.visiting_team?.id) ??
    null

  const handleGameDetailsTapped = () => {
    trackEvent('home_game_card_game_details_tapped', {
      platform: getPlatformType(),
      game_id: rowItem?.id,
      is_first_game: isFirstGame,
    })
  }

  return (
    <div css={cardContainerStyle} key={rowItem.id}>
      <div css={headerStyle}>
        <h2 css={titleStyle}>
          {isFirstGame ? 'Next Game' : 'Ready for your game?'}
        </h2>
        <a href={`/game/${rowItem.id}`} onClick={handleGameDetailsTapped}>
          <div css={viewMoreStyle}>
            <p css={viewMoreTextStyle}>Game details</p>
            <RightOutlined style={{ fontSize: '12px' }} />
          </div>
        </a>
      </div>
      <a href={`/game/${rowItem.id}`} onClick={handleGameDetailsTapped}>
        <TeamsContainer homeTeam={homeTeam} awayTeam={awayTeam} />
        <GameInfoComponent
          isGameTimeToBeDefined={isGameTimeToBeDefined}
          gameDateWithoutTime={gameDateWithoutTime}
          gameTime={gameTime}
          leagueName={leagueName}
          arenaName={arenaName}
          facilityNumber={facilityNumber}
        />
      </a>

      {isPlayingOnHomeTeam ? (
        <TeamSection
          team={rowItem.home_team}
          teamSide='home'
          showHeader={isPlayingOnHomeTeam && isPlayingOnAwayTeam}
          playersAttendance={homePlayers}
          teamRep={homeTeamRep}
          rowItem={rowItem}
          onAttendanceSelect={onAttendanceSelect}
          onPressManageAttendance={onPressManageAttendance}
          onPressManageRefreshments={onPressManageRefreshments}
          onPressManageVideographer={onPressManageVideographer}
        />
      ) : null}

      {/*
        `isPlayingOnAwayTeam` is derived from a player matching
        `visiting_team.id`, so it can only be true when the team exists -- the
        guard narrows it for us, but TypeScript cannot see that through the
        derived boolean, hence the explicit check (SN-8410).
      */}
      {isPlayingOnAwayTeam && rowItem.visiting_team ? (
        <TeamSection
          team={rowItem.visiting_team}
          teamSide='visitor'
          showHeader={isPlayingOnHomeTeam && isPlayingOnAwayTeam}
          playersAttendance={awayPlayers}
          teamRep={awayTeamRep}
          rowItem={rowItem}
          onAttendanceSelect={onAttendanceSelect}
          onPressManageAttendance={onPressManageAttendance}
          onPressManageRefreshments={onPressManageRefreshments}
          onPressManageVideographer={onPressManageVideographer}
        />
      ) : null}
    </div>
  )
}

export default React.memo(GameAttendanceCard)
